import { PHOTO_MAX_SIDE, PHOTO_QUALITY } from '../config';

export interface Prepared {
  blob: Blob;
  name: string;
  mime: string;
}

/** HEIC/HEIF cannot be drawn on a canvas in most browsers: such photos go up as they are. */
export function isHeic(file: { type: string; name: string }): boolean {
  return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

function jpegName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '') || 'photo';
  return `${base}.jpg`;
}

/** Target size keeping the aspect ratio: the longest side ≤ max. */
export function fitSize(w: number, h: number, max = PHOTO_MAX_SIDE): { w: number; h: number } {
  const k = Math.min(1, max / Math.max(w, h, 1));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image decode failed'));
    };
    img.src = url;
  });
}

/**
 * Downscale a camera photo to ≤1600px JPEG before upload (phones make 4–12 MB photos; on a border crossing
 * the network is slow). Non-images, HEIC and anything the browser cannot decode are passed through unchanged.
 */
export async function preparePhoto(file: File): Promise<Prepared> {
  const passthrough: Prepared = { blob: file, name: file.name || 'file', mime: file.type || 'application/octet-stream' };
  if (!file.type.startsWith('image/') || isHeic(file) || file.type === 'image/gif') return passthrough;
  try {
    const img = await loadImage(file);
    const { w, h } = fitSize(img.naturalWidth || img.width, img.naturalHeight || img.height);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return passthrough;
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', PHOTO_QUALITY));
    if (!blob) return passthrough;
    // already small JPEG: keep the original if re-encoding made it bigger
    if (file.type === 'image/jpeg' && blob.size >= file.size) return passthrough;
    return { blob, name: jpegName(file.name || 'photo'), mime: 'image/jpeg' };
  } catch {
    return passthrough;
  }
}
